import React, { useEffect, useState } from 'react';
import '../../../src/Pages/Css/Integration.css';
import 'react-phone-number-input/style.css';
import CustomLayout from '../../layouts/Customlayout';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Modal,
  Button,
  Input,
  Steps,
  message,
  Typography,
  Card,
  Alert,
} from 'antd';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import config from '../../config';

const { Step } = Steps;
const { Title, Text } = Typography;

function loadFacebookSDK() {
  if (document.getElementById('facebook-jssdk')) return;
  const script = document.createElement('script');
  script.id = 'facebook-jssdk';
  script.src = 'https://connect.facebook.net/en_US/sdk.js';
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
}

export default function WhatsApp() {
  const [currentStep, setCurrentStep] = useState(1);
  const [fbObject, setFbObject] = useState({});
  const [subAccountInfo, setSubAccountInfo] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const integrationTranslations = t('integration');

  // On mount: check for existing phone_number in UserObject
  useEffect(() => {
    const userJson = JSON.parse(localStorage.getItem('UserObject'));
    if (userJson?.phone_number_id) {
      setPhoneNumber(userJson.phone_number_id);
      setCurrentStep(3);
      setRegistrationSuccess(true);
    }
  }, []);

  // Init FB SDK & listener
  useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: '<YOUR_FACEBOOK_APP_ID>',
        autoLogAppEvents: true,
        xfbml: false,
        version: 'v16.0',
      });
    };
    loadFacebookSDK();

    const listener = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (['FINISH','FINISH_ONLY_WABA'].includes(data.event)) {
            const { phone_number_id, waba_id } = data.data;
            setFbObject({ phone_number_id, waba_id });
            setCurrentStep(2);
          }
        }
      } catch {}
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  const goBack = () => navigate('/user/integration');

  const handleLogin = () => {
    if (!window.FB) {
      message.error('Facebook SDK not loaded yet. Please try again in a moment.');
      return;
    }
    window.FB.login(
      () => {},
      {
        config_id: '399880686267907',
        auth_type: 'rerequest',
        response_type: 'code',
        override_default_response_type: true,
        extras: { sessionInfoVersion: 2, setup: { solutionID: '969683498163827' } },
      }
    );
  };

  const handleCreateSender = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('friendly_name', 'My WhatsApp Sender');
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${config.apiUrl}whatsapp-subaccount/`,
        formData,
        { headers: { Authorization: `Token ${token}` } }
      );
      setSubAccountInfo(res.data.response);
      setIsModalVisible(true);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      message.error(`Failed to create sender: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const validateProfileName = (name) => name.trim().length > 0 && name.length <= 50;
  const validateCompanyName = (name) => name.trim().length > 0 && name.length <= 100;
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (num) => {
    if (num?.startsWith('+1555')) return true;
    return isValidPhoneNumber(num);
  };

  const handleRegisterSender = async () => {
    const errs = {};
    if (!validateProfileName(profileName)) errs.profileName = 'Invalid profile name.';
    if (!validateCompanyName(companyName)) errs.companyName = 'Invalid company name.';
    if (!validateEmail(companyEmail)) errs.companyEmail = 'Invalid email.';
    if (!validatePhone(phoneNumber)) errs.phoneNumber = 'Invalid phone number.';
    setValidationErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('sub_account_id', subAccountInfo.sid);
      formData.append('sub_account_token', subAccountInfo.auth_token);
      formData.append('waba_id', fbObject.waba_id);
      formData.append('sender_id', phoneNumber.replace('+', ''));
      formData.append('profile_name', profileName);
      formData.append('company_name', companyName);
      formData.append('company_email', companyEmail);
      formData.append('company_website', companyWebsite);

      const token = localStorage.getItem('token');
      await axios.post(
        `${config.apiUrl}whatsapp-sender-register/`,
        formData,
        { headers: { Authorization: `Token ${token}` } }
      );

      // -- CLOSE MODAL on success --
      setIsModalVisible(false);

      setRegistrationSuccess(true);
      setCurrentStep(3);
      message.success('WhatsApp setup complete!');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      message.error(`Registration failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomLayout>
      <div className="p-16 mx-auto bg-gradient-to-r from-blue-50 to-white shadow-xl rounded-xl">
        <Button icon={<ArrowLeftOutlined />} className="mb-6" onClick={goBack}>
          {integrationTranslations.backToIntegrations}
        </Button>

        <Steps current={currentStep - 1} className="mb-6">
          <Step title={integrationTranslations.whatsappTab.connectFbTab} />
          <Step title={integrationTranslations.whatsappTab.connectBusinessTab} />
          <Step title={integrationTranslations.whatsappTab.allSetTab} />
        </Steps>

        <Card className="shadow-md p-6 rounded-lg border-gray-100">
          {currentStep === 1 && (
            <div className="text-center">
              <Alert
                type="info"
                message={
                  <div style={{ textAlign: 'left' }}>
                    <p>{integrationTranslations.whatsappTab.needs.a}:</p>
                    <ul style={{ margin: 0 }}>
                      <li>{integrationTranslations.whatsappTab.needs.b}</li>
                      <li>{integrationTranslations.whatsappTab.needs.c}</li>
                      <li>{integrationTranslations.whatsappTab.needs.d}</li>
                    </ul>
                  </div>
                }
                style={{ marginBottom: 40, maxWidth: 400, margin: '0 auto' }}
              />
              <img
                src="https://www.svgrepo.com/show/419933/whats-app-whatsapp-whatsapp-icon.svg"
                alt="FB Login"
                className="mx-auto mb-4 mt-6 w-24 h-24"
              />
              <Title level={4}>
                {integrationTranslations.whatsappTab.integrationNote}
              </Title>
              <Text className="block text-gray-500">
                {integrationTranslations.whatsappTab.subHeading1}
              </Text>
              <Button type="primary" className="mt-6" onClick={handleLogin}>
                {integrationTranslations.whatsappTab.fbLogin}
              </Button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="text-center">
              <Title level={4}>
                {integrationTranslations.whatsappTab.connectBusinessTab}
              </Title>
              <Text className="block text-gray-500">
                {integrationTranslations.whatsappTab.createNote}
              </Text>
              <Button
                type="primary"
                className="mt-6"
                loading={loading}
                onClick={handleCreateSender}
              >
                Create Sender
              </Button>
            </div>
          )}

          {currentStep === 3 && registrationSuccess && (
            <div className="text-center">
              <CheckCircleOutlined className="text-green-500 text-4xl mb-2" />
              <Title level={3} className="text-green-500">
                {integrationTranslations.whatsappTab.allSet}
              </Title>
              <Text className="block text-gray-500">
                {integrationTranslations.whatsappTab.successNote}
              </Text>
            </div>
          )}
        </Card>

        <Modal
          title="Register Sender"
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsModalVisible(false)}>
              {integrationTranslations.whatsappTab.cancelBtn}
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleRegisterSender}
            >
              {integrationTranslations.whatsappTab.submitBtn || 'Submit'}
            </Button>,
          ]}
        >
          <label htmlFor="profileName">
            {integrationTranslations.whatsappTab.profileName}
          </label>
          <Input
            id="profileName"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            placeholder={integrationTranslations.whatsappTab.profileNamePlaceholder}
          />
          {validationErrors.profileName && (
            <Text type="danger">{validationErrors.profileName}</Text>
          )}

          <label htmlFor="companyName" style={{ marginTop: 16 }}>
            {integrationTranslations.whatsappTab.companyName}
          </label>
          <Input
            id="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={integrationTranslations.whatsappTab.companyNamePlaceholder}
          />
          {validationErrors.companyName && (
            <Text type="danger">{validationErrors.companyName}</Text>
          )}

          <label htmlFor="companyEmail" style={{ marginTop: 16 }}>
            {integrationTranslations.whatsappTab.companyEmail}
          </label>
          <Input
            id="companyEmail"
            value={companyEmail}
            onChange={(e) => setCompanyEmail(e.target.value)}
            placeholder={integrationTranslations.whatsappTab.companyEmailPlaceholder}
          />
          {validationErrors.companyEmail && (
            <Text type="danger">{validationErrors.companyEmail}</Text>
          )}

          <label style={{ marginTop: 16 }}>
            {integrationTranslations.whatsappTab.phoneNumber}
          </label>
          <PhoneInput
            international
            defaultCountry="US"
            placeholder={integrationTranslations.whatsappTab.phoneNumberPlaceholder}
            value={phoneNumber}
            onChange={setPhoneNumber}
          />
          {validationErrors.phoneNumber && (
            <Text type="danger">{validationErrors.phoneNumber}</Text>
          )}
        </Modal>
      </div>
    </CustomLayout>
  );
}
